import React, { type ReactNode } from 'react';
import { Text } from '@atlaskit/primitives/compiled';
import type { Flag } from '@atlassian/jira-flags';
import { STATUS_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { GroupContextField } from '../types.tsx';
import messages from './messages.tsx';
import type { CreatedIssue, OnSortChangeFn, FireAnalyticsFn } from './types.tsx';

/**
 * @deprecated
 * This function is deprecated and will be removed in a future release in favour of fieldValueContainsGroupContextValue.
 */
export const getCreatedIssueFieldValue = (
	createdIssue: CreatedIssue,
	fieldId: string,
): string | null => {
	const issueField = createdIssue.fieldsByIdOrAlias?.find((field) => field?.fieldId === fieldId);

	switch (issueField?.__typename) {
		case 'JiraStatusField':
			return issueField.status?.statusId ?? null;
		case 'JiraPriorityField':
			return issueField.priority?.priorityId ?? null;
		case 'JiraSingleSelectUserPickerField':
			return issueField.user?.accountId ?? null;
		case 'JiraSingleSelectField':
			return issueField.fieldOption?.optionId ?? null;
		case 'JiraSprintField':
			return issueField.selectedSprintsConnection?.edges?.[0]?.node?.sprintId ?? null;
		case 'JiraNumberField':
			return issueField.number?.toString() ?? null;
		default:
			return null;
	}
};

export const fieldValueContainsGroupContextValue = (
	createdIssue: CreatedIssue,
	groupContextField: GroupContextField,
	fieldId: string,
) => {
	const issueField = createdIssue.fieldsByIdOrAlias?.find((field) => field?.fieldId === fieldId);

	let fieldValue: string | string[] | null;

	switch (issueField?.__typename) {
		case 'JiraStatusField':
			fieldValue = issueField.status?.statusId ?? null;
			break;
		case 'JiraPriorityField':
			fieldValue = issueField.priority?.priorityId ?? null;
			break;
		case 'JiraSingleSelectUserPickerField':
			fieldValue = issueField.user?.accountId ?? null;
			break;
		case 'JiraSingleSelectField':
			fieldValue = issueField.fieldOption?.optionId ?? null;
			break;
		case 'JiraSprintField':
			fieldValue = issueField.selectedSprintsConnection?.edges
				? issueField.selectedSprintsConnection.edges
						.map((edge) => edge?.node?.sprintId)
						.filter((sprintId): sprintId is string => !!sprintId)
				: null;
			break;
		case 'JiraNumberField':
			fieldValue = issueField.number?.toString() ?? null;
			break;
		case 'JiraLabelsField':
			fieldValue = issueField.selectedLabelsConnection?.edges
				? issueField.selectedLabelsConnection.edges
						.map((edge) => edge?.node?.name)
						.filter((name): name is string => !!name)
				: null;
			break;
		default:
			fieldValue = null;
	}

	return (
		fieldValue === groupContextField.fieldValue ||
		(groupContextField.fieldValue && fieldValue?.includes(groupContextField.fieldValue))
	);
};

export const getStatusName = (createdIssue: CreatedIssue) => {
	const issueField = createdIssue.fieldsByIdOrAlias?.find(
		(field) => field?.fieldId === STATUS_TYPE,
	);

	return issueField?.status?.name;
};

const Bold = (chunk: ReactNode) => <Text as="strong">{chunk}</Text>;
export const getIssueCreateSuccessFlag = ({
	issueKey,
	statusName,
}: {
	issueKey: string;
	statusName?: string | null;
}): Flag => ({
	id: 'issue-table-grouping-issue-create-utils.use-should-skip-issues-connection-update.flag.success.show-flag.success.flag.success.issue-create-success',
	type: 'success',
	messageId:
		'issue-table-grouping-issue-create-utils.use-should-skip-issues-connection-update.flag.success.show-flag.success.flag.success.issue-create-success',
	messageType: 'transactional',
	isAutoDismiss: true,
	title: [messages.workItemCreatedSuccessfullyTitle, { issueKey }],
	description: statusName
		? [messages.workItemChangedStatusDescription, { issueKey, statusName, Bold }]
		: undefined,
	actions: [
		{
			content: [messages.viewWorkItem, { issueKey }],
			href: `/browse/${issueKey}`,
		},
	],
});

export const getIssueCreateRerankingPromptSuccessFlag = ({
	issueKey,
	onSortChange,
	rankJqlTerm,
	fireApplyRankingAnalytics,
	fireViewWorkItemUIAnalytics,
}: {
	issueKey: string;
	onSortChange?: OnSortChangeFn;
	rankJqlTerm?: string | null;
	fireApplyRankingAnalytics: FireAnalyticsFn;
	fireViewWorkItemUIAnalytics: FireAnalyticsFn;
}): Flag => ({
	id: 'issue-table-grouping-issue-create-utils.use-should-skip-issues-connection-update.flag.success.show-flag.success.flag.success.issue-create-reranking-prompt-success',
	type: 'success',
	messageId:
		'issue-table-grouping-issue-create-utils.use-should-skip-issues-connection-update.flag.success.show-flag.success.flag.success.issue-create-reranking-prompt-success',
	messageType: 'transactional',
	isAutoDismiss: true,
	title: [messages.workItemCreatedSuccessfullyTitle, { issueKey }],
	description: [messages.rerankingPromptDescription, { issueKey, Bold }],
	actions: [
		{
			content: messages.applyRanking,
			onClick: () => {
				fireApplyRankingAnalytics();
				onSortChange?.(rankJqlTerm ?? 'rank', 'ASC');
			},
		},
		{
			content: [messages.viewWorkItem, { issueKey }],
			href: `/browse/${issueKey}`,
			onClick: () => {
				fireViewWorkItemUIAnalytics();
			},
		},
	],
});

export const getIssueCreateHiddenFlag = ({ issueKey }: { issueKey: string }): Flag => ({
	id: 'issue-table-grouping-issue-create-utils.use-should-skip-issues-connection-update.flag.success.show-flag.success.flag.success.issue-create-hidden',
	type: 'success',
	messageId:
		'issue-table-grouping-issue-create-utils.use-should-skip-issues-connection-update.flag.success.show-flag.success.flag.success.issue-create-hidden',
	messageType: 'transactional',
	isAutoDismiss: true,
	title: [messages.workItemCreatedSuccessfullyTitle, { issueKey }],
	description: [
		fg('jira-issue-terminology-refresh-m3')
			? messages.workItemHiddenDescription
			: messages.issueHiddenDescription,
		{ issueKey, Bold },
	],
	actions: [
		{
			content: [messages.viewWorkItem, { issueKey }],
			href: `/browse/${issueKey}`,
		},
	],
});
